import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserModelData, User, PAGE_NAME } from "../../../types";
import { useTranslationHook } from "../../hooks/useTranslationHook";
import { useNavigate } from "react-router-dom";
import { FONTSIZE, mainCryptoName } from "../../../constants";
import { THEME_COLORS } from "../../themes/DarkLight";
import useThemeHook from "../../hooks/useThemeHook";
import arthereumProvider from "../../../backend/ArthereumProvider";
import { actions } from "../../redux";
import {
  LocalStorageController,
  LOCAL_STORAGE_DATA,
} from "../../utils/LocalStorageController";
import LoadingIndicator from "../../components/LoadingIndicator";
import Text from "../../components/ThemedText";
interface ActionCard {
  title: string;
  description: string;
  icon: string;
  action: () => void;
  completed: boolean;
}

const ProfilePage = ({}) => {
  const [refreshing, setRefreshing] = useState(false);
  const userData: UserModelData = useSelector(
    (state) => (state as any)?.actualUser
  );

  const navigate = useNavigate();
  const getTranslationText = useTranslationHook();

  const isPro = userData?.user?.isLifetimePro || (userData?.user?.isPro && userData?.user?.proExpiry && new Date((userData?.user?.proExpiry as any).seconds * 1000) > new Date());

  const actionCards: ActionCard[] = [
    {
      title: getTranslationText("invite_friends_and_family"),
      description: getTranslationText("invite_friends_and_family_description"),
      icon: "people",
      action: () => navigate(PAGE_NAME.INVITE_USERS),
      completed: false,
    },
    ...(!isPro ? [{
      title: "Upgrade to Arthereum Pro 👑",
      description: "Unlock 2x Multiplier, Ad-Free experience & Elite status badges.",
      icon: "crown",
      action: () => navigate(PAGE_NAME.PRO),
      completed: false,
    }] : []),
    {
      title: getTranslationText("prove_humanness"),
      description: getTranslationText("prove_humanness_desc"),
      icon: "user",
      action: () => alert(getTranslationText("prove_humanness_desc")),
      completed: false,
    },
    {
      title: getTranslationText("complete_profile"),
      description: getTranslationText("complete_profile_desc"),
      icon: "edit",
      action: () => console.log("Complete profile"),
      completed: userData?.user?.profileImage?.length > 2,
    },
  ];

  const formatBalance = (balance: number) => {
    return balance.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const logout = useCallback(async () => {
    try {
      // Modal simulation using state
      const shouldLogout = window.confirm(
        `${getTranslationText("logout_description")}\n\n` +
          `${getTranslationText("logout")}?`
      );
      if (!shouldLogout) return;

      setIsLoading(true);
      try {
        await arthereumProvider.logout(userData?.user?.id);
        dispatch(actions.resetStore(undefined));
        LocalStorageController.connect().storeData(
          LOCAL_STORAGE_DATA.SYSTEM_FULLY_INITIALIZED,
          "false"
        );
        // Navigation logic for React Router
        navigate("/", { replace: true });
      } catch (error) {
        console.log("error");
        window.alert(getTranslationText("error"));
      } finally {
        setIsLoading(false);
      }
    } catch (error) {
      throw error;
    }
  }, []);

  const refresh = async () => {
    if (refreshing) return;
    try {
      setRefreshing(true);
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
    } catch (error) {
      console.error(error);
    } finally {
      setRefreshing(false);
    }
  };
  const theme = useThemeHook();

  const ActionCard = ({
    title,
    description,
    icon,
    action,
    completed,
  }: {
    title: string;
    description: string;
    icon: any;
    action: any;
    completed: boolean;
  }) => (
    <div
      onClick={action}
      style={{
        backgroundColor: THEME_COLORS[theme].component_background_color,
        borderRadius: "16px",
        marginBottom: "12px",
        cursor: "pointer",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        opacity: completed ? 0.7 : 1,
        transition: "opacity 0.2s ease",
        border: title.includes("Pro") ? (theme === "dark" ? "1px solid rgba(251, 191, 36, 0.3)" : "1.5px solid #FBBF24") : "none",
        background: title.includes("Pro") ? (theme === "dark" ? "linear-gradient(to right, #1e293b, #0f172a)" : "linear-gradient(to right, #FFFBEB, #FFFFFF)") : THEME_COLORS[theme].component_background_color,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "16px",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "20px",
            backgroundColor: title.includes("Pro") ? "#FEF3C7" : "#F2F2F7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "12px",
            boxShadow: title.includes("Pro") ? "0 0 10px rgba(245, 158, 11, 0.2)" : "none",
          }}
        >
          <span style={{ color: completed ? "#8E8E93" : (title.includes("Pro") ? "#D97706" : "#007AFF"), fontSize: "18px" }}>
            {icon === "people" && "👥"}
            {icon === "mail" && "✉️"}
            {icon === "user" && "👤"}
            {icon === "edit" && "✏️"}
            {icon === "crown" && "👑"}
          </span>
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: 700,
              color: title.includes("Pro") ? (theme === "dark" ? "#FBBF24" : "#D97706") : THEME_COLORS[theme].inverse_theme,
              marginBottom: "4px",
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: THEME_COLORS[theme].gray,
              lineHeight: "16px",
              fontWeight: "500"
            }}
          >
            {description}
          </div>
        </div>
        <div style={{ color: completed ? "#34C759" : (title.includes("Pro") ? "#F59E0B" : "#8E8E93"), fontWeight: "bold" }}>
          {completed ? "✓" : "→"}
        </div>
      </div>
    </div>
  );

  return (
    <div
      style={{
        padding: "13px",
        backgroundColor: THEME_COLORS[theme].page_background_color,
        paddingBottom: "100px",
      }}
    >
      <div
        style={{
          textAlign: "center",
          paddingTop: "24px",
          paddingBottom: "24px",
        }}
      >
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <img
            src={
              userData.user.profileImage ??
              "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_960_720.png"
            }
            alt="Profile"
            style={{
              width: "150px",
              height: "150px",
              borderRadius: "40px",
              objectFit: "cover",
              backgroundColor: THEME_COLORS[theme].component_background_color,
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
              border: isPro ? "4px solid #F59E0B" : "none",
              boxShadow: isPro ? "0 0 20px rgba(245, 158, 11, 0.3)" : "none",
            }}
          />
          {isPro && (
             <div style={{
               position: 'absolute',
               bottom: '5px',
               right: '5px',
               backgroundColor: '#F59E0B',
               color: 'white',
               fontSize: '10px',
               fontWeight: 'bold',
               padding: '4px 8px',
               borderRadius: '12px',
               boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
             }}>
               PRO 👑
             </div>
          )}
        </div>
        <div
          style={{
            fontSize: FONTSIZE.SECONDARY,
            fontWeight: "700",
            paddingTop: "15px",
            wordBreak: "break-word",
            color: THEME_COLORS[theme].inverse_theme,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
        >
          @{userData.user.username}
          {isPro && (
            <span style={{ 
              background: 'linear-gradient(45deg, #F59E0B, #FBBF24)', 
              color: 'white', 
              fontSize: '10px', 
              padding: '2px 8px', 
              borderRadius: '20px', 
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Elite
            </span>
          )}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            flex: 1,
            padding: "16px",
            backgroundColor: THEME_COLORS[theme].component_background_color,
            borderRadius: "16px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Text
            text={getTranslationText("crypto_balance", { mainCryptoName })}
            style={{ fontSize: "14px", fontWeight: "500", opacity: 0.9 }}
          ></Text>
          <Text
            text={formatBalance(userData.user.arthereumBalance)}
            style={{ fontSize: "24px", fontWeight: "500", marginTop: "8px" }}
          ></Text>
        </div>

        <div
          style={{
            flex: 1,
            padding: "16px",
            backgroundColor: THEME_COLORS[theme].component_background_color,
            borderRadius: "16px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              color: THEME_COLORS[theme].inverse_theme,
              fontWeight: "500",
              opacity: 0.9,
            }}
          >
            {getTranslationText("crypto_balance", { mainCryptoName: "PiCoin" })}
          </div>
          <Text
            text={formatBalance(userData.user.picoinBalance)}
            style={{ fontSize: "24px", fontWeight: "500", marginTop: "8px" }}
          ></Text>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          padding: "16px",
          backgroundColor: THEME_COLORS[theme].component_background_color,
          marginBottom: "24px",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "20px",
              fontWeight: "500",
              color: THEME_COLORS[theme].inverse_theme,
            }}
          >
            {userData?.user?.miningSessionsCount ?? 0}
          </div>
          <div
            style={{
              fontSize: "14px",
              color: THEME_COLORS[theme].gray,
              marginTop: "4px",
            }}
          >
            {getTranslationText("mining_sessions")}
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "20px",
              fontWeight: "500",
              color: THEME_COLORS[theme].inverse_theme,
            }}
          >
            {userData.user.referalsCount}
          </div>
          <div
            style={{
              fontSize: "14px",
              color: THEME_COLORS[theme].gray,
              marginTop: "4px",
            }}
          >
            {getTranslationText("referrals")}
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "20px",
              color: THEME_COLORS[theme].inverse_theme,
              fontWeight: "500",
            }}
          >
            {userData.user.stakeBoost}x
          </div>
          <div
            style={{
              fontSize: "14px",
              color: THEME_COLORS[theme].gray,
              marginTop: "4px",
            }}
          >
            {getTranslationText("staking_boost")}
          </div>
        </div>
      </div>

      <div style={{ marginTop: "24px" }}>
        <h2
          style={{
            fontSize: "20px",
            fontWeight: "500",
            marginBottom: "16px",
            color: THEME_COLORS[theme].inverse_theme,
          }}
        >
          {getTranslationText("must_do")}
        </h2>
        {actionCards.map((card, index) => (
          <ActionCard key={index} {...card} />
        ))}
      </div>
      <div style={{ marginTop: "24px" }}>
        <h2
          onClick={logout}
          style={{
            fontSize: "20px",
            fontWeight: "500",
            marginBottom: "16px",
            color: THEME_COLORS[theme].red,
          }}
        >
          {getTranslationText("logout")}
        </h2>
        {isLoading && <LoadingIndicator />}
        {/* <p>{JSON.stringify(userData, null, 2)}</p> */}
      </div>
    </div>
  );
};

export default ProfilePage;
